import {
  SUPPORT_CHAT_API_BASE,
  SUPPORT_CHAT_APP_ID,
  SUPPORT_CHAT_TRIGGER_ID,
} from "./support.config";

let isInitiated = false;

let bufferedAuthTokens: Record<string, string> | null = null;

export const INTERCOM_SHOW_TRIGGER_EVENT = "@repeatmd/show-trigger-event";

export const INTERCOM_HIDE_TRIGGER_EVENT = "@repeatmd/hide-trigger-event";

const checkIsIntercomLoaded = () => Boolean(globalThis.Intercom);

const checkIfShouldRunPostInitCommand = () => checkIsIntercomLoaded() && isInitiated;

const invokeSetAuthTokens = (tokens: Record<string, string>) => {
  (
    globalThis.Intercom as unknown as (
      command: "setAuthTokens",
      tokens: Record<string, string>,
    ) => void
  )("setAuthTokens", tokens);
};

const flushBufferedAuthTokens = () => {
  if (!bufferedAuthTokens || Object.keys(bufferedAuthTokens).length === 0) {
    return;
  }
  if (!checkIfShouldRunPostInitCommand()) {
    return;
  }

  invokeSetAuthTokens(bufferedAuthTokens);
  bufferedAuthTokens = null;
};

export const initIntercomWidget = (payload: Intercom_.IntercomSettings = {}) => {
  if (isInitiated || !checkIsIntercomLoaded()) {
    return;
  }

  globalThis.Intercom("boot", {
    app_id: SUPPORT_CHAT_APP_ID,
    api_base: SUPPORT_CHAT_API_BASE,
    hide_default_launcher: true,
    custom_launcher_selector: `#${SUPPORT_CHAT_TRIGGER_ID}`,
    ...payload,
  });

  isInitiated = true;

  flushBufferedAuthTokens();
};

// Registers the Messenger onShow callback (replaces any previously registered one).
export const onIntercomShow = (callback: () => void) => {
  if (!checkIfShouldRunPostInitCommand()) {
    return;
  }

  (globalThis.Intercom as unknown as (command: "onShow", callback: () => void) => void)(
    "onShow",
    callback,
  );
};

export const updateIntercomWidget = (payload: Intercom_.IntercomSettings) => {
  if (!checkIfShouldRunPostInitCommand()) {
    return;
  }

  globalThis.Intercom("update", payload);
};

// Registers named auth tokens that custom data connectors (including the MCP
// connector consumed by Fin) reference from their HTTP Headers configuration.
// Tokens are session-scoped, never persisted to the Intercom user record, and
// can be re-registered at any time to reflect rotated JWTs or a new platform.
//
// If called before `initIntercomWidget` has booted Intercom, the tokens are
// buffered and flushed once boot completes — this prevents a silent drop when
// the MCP JWT mint resolves before the boot effect runs.
//
// Cast is required because `setAuthTokens` is not yet declared in
// @types/intercom-web v2.x; remove the cast once the typings catch up.
export const setIntercomAuthTokens = (tokens: Record<string, string | undefined>) => {
  const definedTokens: Record<string, string> = {};
  for (const [name, value] of Object.entries(tokens)) {
    if (value !== undefined && value !== "") {
      definedTokens[name] = value;
    }
  }

  if (Object.keys(definedTokens).length === 0) {
    return;
  }

  if (!checkIfShouldRunPostInitCommand()) {
    bufferedAuthTokens = { ...(bufferedAuthTokens ?? {}), ...definedTokens };
    return;
  }

  invokeSetAuthTokens(definedTokens);
};

export const showIntercomWidget = () => {
  if (!checkIfShouldRunPostInitCommand()) {
    return;
  }

  globalThis.dispatchEvent(new CustomEvent(INTERCOM_SHOW_TRIGGER_EVENT));
};

export const hideIntercomWidget = () => {
  if (!checkIfShouldRunPostInitCommand()) {
    return;
  }

  globalThis.dispatchEvent(new CustomEvent(INTERCOM_HIDE_TRIGGER_EVENT));
};

export const logoutIntercomWidget = () => {
  if (!checkIfShouldRunPostInitCommand()) {
    return;
  }

  globalThis.Intercom("shutdown");
  isInitiated = false;
  bufferedAuthTokens = null;
};

export const openIntercomMessenger = () => {
  if (!checkIfShouldRunPostInitCommand()) {
    return;
  }

  globalThis.Intercom("showMessages");
};
